import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconPrinter } from '@tabler/icons-react';

import {
  useLazyGetPatientSchedulesQuery,
  useUpdatePatientMutation
} from '@/store/slices/api/patientsApiSlice';
import {
  buildPrintLetterValues,
  fillPrintLetter,
  printLetterHtml,
  scheduleDateSlotsIn,
  usesInductivePrintLetter
} from '@/utils/printLetter';
import PrintLetterPreview from '@/components/PrintLetterPreview';

import { loadPrintLetterTemplate, printLetterErrorMessage } from '@/services/printLetters';

import type { Patient } from '@/types/patient';
import type { ResolvedPrintLetters } from '@/types/printLetter';

import { loadPrintLetterDates } from './loadPrintLetterDates';

import styles from './PrintLetterModal.module.scss';

const DATE_SLOTS = [1, 2, 3, 4];
const DATE_PATTERN = /^\d{2}-\d{2}-\d{4}$/;

interface PrintLetterModalProps {
  opened: boolean;
  onClose: () => void;
  patient: Patient;
  printLetters: ResolvedPrintLetters;
}

type TemplateState =
  | { status: 'loading' }
  | { status: 'ready'; html: string }
  | { status: 'error'; message: string };

const PrintLetterModal = ({ opened, onClose, patient, printLetters }: PrintLetterModalProps) => {
  const letters = printLetters.letters;
  const [letterId, setLetterId] = useState(letters[0]?.id ?? '');
  const [template, setTemplate] = useState<TemplateState>({ status: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);
  const templates = useRef(new Map<string, string>());

  const [dates, setDates] = useState<string[]>(['', '', '', '']);
  const [datesLoading, setDatesLoading] = useState(false);
  const [note, setNote] = useState(patient.patientPrintNote ?? '');
  const [savedNote, setSavedNote] = useState(patient.patientPrintNote ?? '');
  const [printing, setPrinting] = useState(false);

  const [loadSchedules] = useLazyGetPatientSchedulesQuery();
  const [updatePatient, { isLoading: isSavingNote }] = useUpdatePatientMutation();

  const activeLetter = letters.find((letter) => letter.id === letterId) ?? letters[0];
  const activeLetterId = activeLetter?.id ?? '';
  const inductive = usesInductivePrintLetter(patient.matchDevices);

  // Fresh fields each time the modal opens. A background refetch of the patient while it is open
  // must not overwrite what staff have typed.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (opened && !wasOpen.current) {
      const saved = patient.patientPrintNote ?? '';
      setNote(saved);
      setSavedNote(saved);
      setLetterId((current) =>
        letters.some((letter) => letter.id === current) ? current : (letters[0]?.id ?? '')
      );
    }
    wasOpen.current = opened;
  }, [opened, patient.patientPrintNote, letters]);

  // Next routine dates, the same rows the schedule PDF uses. Editable for this print only.
  useEffect(() => {
    if (!opened || !patient.id) return;
    let cancelled = false;
    setDatesLoading(true);
    void loadPrintLetterDates((page, limit) =>
      loadSchedules({ patientId: patient.id, status: 'scheduled', limit, page, order: 'ASC' })
        .unwrap()
        .then((result) => result.data ?? [])
        .catch(() => [])
    )
      .then((upcoming) => {
        if (!cancelled) setDates(DATE_SLOTS.map((slot) => upcoming[slot - 1] ?? ''));
      })
      .finally(() => {
        if (!cancelled) setDatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [opened, patient.id, loadSchedules]);

  useEffect(() => {
    if (!opened || !activeLetterId) return;
    const cacheKey = `${activeLetterId}:${inductive ? 'inductive' : 'standard'}`;
    const cached = templates.current.get(cacheKey);
    if (cached) {
      setTemplate({ status: 'ready', html: cached });
      return;
    }
    let cancelled = false;
    setTemplate({ status: 'loading' });
    void loadPrintLetterTemplate(printLetters.organizationId, activeLetterId, inductive)
      .then((loaded) => {
        templates.current.set(cacheKey, loaded.html);
        if (!cancelled) setTemplate({ status: 'ready', html: loaded.html });
      })
      .catch((error: unknown) => {
        if (!cancelled) setTemplate({ status: 'error', message: printLetterErrorMessage(error) });
      });
    return () => {
      cancelled = true;
    };
  }, [opened, activeLetterId, inductive, printLetters.organizationId, reloadKey]);

  const templateHtml = template.status === 'ready' ? template.html : '';
  const dateSlots = useMemo(() => scheduleDateSlotsIn(templateHtml), [templateHtml]);

  const filledHtml = useMemo(
    () =>
      templateHtml
        ? fillPrintLetter(
            templateHtml,
            buildPrintLetterValues({ patient, scheduleDates: dates, note })
          )
        : '',
    [templateHtml, patient, dates, note]
  );
  const [previewHtml] = useDebouncedValue(filledHtml, 250);

  const isNoteUnchanged = note === savedNote;
  const badDates = dateSlots.filter((slot) => {
    const value = dates[slot - 1]?.trim();
    return Boolean(value) && !DATE_PATTERN.test(value);
  });

  const setDate = (slot: number, value: string) =>
    setDates((current) => current.map((item, index) => (index === slot - 1 ? value : item)));

  const saveNote = async (): Promise<boolean> => {
    if (isNoteUnchanged) return true;
    try {
      await updatePatient({ patient: { id: patient.id, patientPrintNote: note } }).unwrap();
      setSavedNote(note);
      notifications.show({
        title: 'Success',
        message: 'Note saved successfully',
        color: 'green',
        position: 'top-center'
      });
      return true;
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to save note. Please try again.',
        color: 'red',
        position: 'top-center'
      });
      return false;
    }
  };

  const handlePrint = async () => {
    if (!filledHtml || printing) return;
    setPrinting(true);
    try {
      // The note is saved with the letter so the next print starts from it. A failed save still
      // prints what is on screen.
      await saveNote();
      await printLetterHtml(filledHtml);
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Print letter',
        message: error instanceof Error ? error.message : 'The letter could not be printed.',
        color: 'red',
        position: 'top-center'
      });
    } finally {
      setPrinting(false);
    }
  };

  const letterTitle = activeLetter?.name || 'Print letter';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="1100px"
      centered
      closeOnClickOutside={false}
      title={
        <Text fw={700} size="lg">
          {letterTitle}
        </Text>
      }>
      <div className={styles.body}>
        <Box className={styles.previewColumn}>
          {template.status === 'loading' && (
            <Group justify="center" mih={320}>
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Loading the letter…
              </Text>
            </Group>
          )}
          {template.status === 'error' && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />}>
              <Stack gap="xs" align="flex-start">
                <Text size="sm">{template.message}</Text>
                <Button
                  size="compact-sm"
                  variant="light"
                  color="red"
                  onClick={() => setReloadKey((key) => key + 1)}>
                  Try again
                </Button>
              </Stack>
            </Alert>
          )}
          {template.status === 'ready' && (
            <PrintLetterPreview html={previewHtml || filledHtml} title={`${letterTitle} preview`} />
          )}
        </Box>

        <Stack className={styles.fieldsColumn} gap="md">
          {letters.length > 1 && (
            <Select
              label="Letter"
              data={letters.map((letter) => ({ value: letter.id, label: letter.name }))}
              value={activeLetterId}
              onChange={(value) => value && setLetterId(value)}
              allowDeselect={false}
            />
          )}

          <Box>
            <Textarea
              label="Patient note"
              description="Saved to the patient and printed on the letter."
              placeholder="Add a note for this patient's letter…"
              autosize
              minRows={3}
              maxRows={8}
              value={note}
              onChange={(event) => setNote(event.currentTarget.value)}
              disabled={isSavingNote}
            />
            <Group justify="flex-end" mt={6}>
              <Button
                size="compact-sm"
                variant="light"
                color="plum"
                onClick={() => {
                  void saveNote();
                }}
                disabled={isNoteUnchanged}
                loading={isSavingNote}>
                Save note
              </Button>
            </Group>
          </Box>

          {dateSlots.length > 0 && (
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Schedule dates
              </Text>
              <Text size="xs" c="dimmed">
                {datesLoading
                  ? 'Checking the schedule…'
                  : 'Filled from the schedule. One visit also fills the next two from its frequency. Changes apply to this print only.'}
              </Text>
              {dateSlots.map((slot) => (
                <TextInput
                  key={slot}
                  aria-label={`Schedule date ${slot}`}
                  leftSection={
                    <Text size="xs" c="dimmed">
                      {slot}
                    </Text>
                  }
                  placeholder="MM-DD-YYYY"
                  value={dates[slot - 1] ?? ''}
                  onChange={(event) => setDate(slot, event.currentTarget.value)}
                  error={badDates.includes(slot) ? 'Use MM-DD-YYYY' : undefined}
                  disabled={datesLoading}
                />
              ))}
            </Stack>
          )}

          <Text size="xs" c="dimmed">
            Prints on US Letter paper. To keep a copy, choose “Save as PDF” in the print dialog.
          </Text>

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button
              color="plum"
              leftSection={<IconPrinter size={16} />}
              onClick={() => {
                void handlePrint();
              }}
              loading={printing}
              disabled={template.status !== 'ready' || datesLoading}>
              Print
            </Button>
          </Group>
        </Stack>
      </div>
    </Modal>
  );
};

export default PrintLetterModal;
